import { Link } from 'react-router-dom';
import { ArrowRight, Calendar } from 'lucide-react';
import blogPostsData from '../content/blog.json';

interface BlogPost {
  id: string;
  title: string;
  slug: string;
  body: any;
  excerpt?: string;
  category?: string;
  featuredImage?: {
    url: string;
    title?: string;
  } | null;
  publishDate: string;
  featured?: boolean;
  createdAt: string;
  updatedAt: string;
}

export function NewsSection() {
  // Get news posts from static data
  const allPosts = blogPostsData as BlogPost[];
  const newsPosts = allPosts
    .filter(post => post.category === 'News')
    .slice(0, 2); // Get first 2 news posts only

  function formatDate(dateString: string) {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  }

  function getImageUrl(url: string): string {
    return url.startsWith('//') ? `https:${url}` : url;
  }

  if (newsPosts.length === 0) {
    return null;
  }

  return (
    <div className="my-16">
      <div className="max-w-[1400px] mx-auto">
        <h2 className="text-2xl font-semibold text-gray-900 mb-8">
          Latest News from Playlist Partner
        </h2>
        
        <div className="grid md:grid-cols-3 gap-6">
          {newsPosts.map((post) => (
            <Link
              key={post.id}
              to={`/blog/news/${post.slug}`}
              className="group bg-white border border-gray-200 rounded-lg overflow-hidden hover:border-green-500 hover:shadow-md transition-all"
            >
              {post.featuredImage?.url && (
                <div className="aspect-[3/2] overflow-hidden">
                  <img 
                    src={getImageUrl(post.featuredImage.url)} 
                    alt={post.featuredImage.title || post.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
              )}
              
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 group-hover:text-green-600 transition-colors line-clamp-2 mb-3">
                  {post.title}
                </h3>
                
                {post.excerpt && (
                  <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                    {post.excerpt}
                  </p>
                )}
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1 text-xs text-gray-500">
                    <Calendar className="w-3 h-3" />
                    <span>{formatDate(post.publishDate || post.createdAt)}</span>
                  </div>
                  <span className="text-green-600 text-sm flex items-center gap-1 group-hover:gap-2 transition-all">
                    Read
                    <ArrowRight className="w-4 h-4" />
                  </span>
                </div>
              </div>
            </Link>
          ))}
          
          {/* More News Card */}
          <Link
            to="/blog"
            className="group bg-gradient-to-br from-green-50 to-green-100 border border-green-200 rounded-lg overflow-hidden hover:border-green-500 hover:shadow-md transition-all flex flex-col"
          >
            <div className="flex-1 flex flex-col justify-center items-center text-center p-6">
              <div className="mb-4">
                <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                  <ArrowRight className="w-8 h-8 text-white" />
                </div>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                View All Posts
              </h3>
              <p className="text-gray-600 text-sm">
                Explore all blog posts and updates
              </p>
            </div>
          </Link>
        </div>
      </div>
    </div>
  );
}